import { ProfileTileProps } from '@christies/react/dist/lib/components/Tiles/ProfileTile/ProfileTile.types'
import { ComponentStatus } from '@christies/react/typings'
import { useMockData } from '../../useMockData'
import useSWR from 'swr'
import { dataFetcher } from '../../dataFetcher'
import { useRelatedContentData } from '../../useRelatedContentData'
import { RelatedDepartmentsKey, ParsedDepartment, Department } from './types'
import { sanitizeUrl } from '../../utils'

const urlDecoder = ({ url, name, id }: { url: string; name?: string; id?: string }): string => {
  const decoder: Record<string, string | undefined> = {
    '{department_name}': name,
    '{department_id}': id,
  }

  return url?.replace(/{department_name}|{department_id}/gi, (match) => decoder[match] as string).toLowerCase()
}

export const useRelatedDepartmentsData = (
  key: RelatedDepartmentsKey,
  mockPromise: Promise<{ default: ProfileTileProps[] }>
): { status: ComponentStatus; data?: ProfileTileProps[] } => {
  const { storyAPIEndpoint, departmentsAPIEndpoint, departmentUrl } = key
  const { data: mockData, status: mockStatus } = useMockData<ProfileTileProps[]>(mockPromise)
  const { data: relatedContentData, status: relatedContentStatus } = useRelatedContentData(
    mockStatus === 'error' ? storyAPIEndpoint : null
  )

  const relatedDepartments = relatedContentData?.relations?.find(
    (content) => content?.type?.toLowerCase() === 'departments'
  )?.values

  const { data, isLoading, error } = useSWR(
    relatedContentStatus === 'ready' && relatedDepartments?.length ? departmentsAPIEndpoint : null,
    dataFetcher
  )

  if (mockStatus !== 'error') {
    return { data: mockData, status: mockStatus }
  }

  const selectedDepartments = relatedDepartments
    ?.map((id: string) => (data as Department[])?.find(({ department_id }: Department) => department_id === id))
    ?.filter((nextDepartment: Department | undefined) => nextDepartment !== undefined) as Department[]

  const parsedData: ParsedDepartment[] = selectedDepartments?.map(
    ({ department_name, department_id, image_url }: Department) => {
      const href = urlDecoder({
        url: departmentUrl,
        name: sanitizeUrl(department_name),
        id: department_id,
      })

      return {
        image: { src: image_url, alt: department_name },
        primaryTitle: department_name,
        href,
      }
    }
  )

  const getComponentStatus = (): ComponentStatus => {
    if (isLoading || relatedContentStatus === 'initial') {
      return 'initial'
    }
    if (error || !parsedData?.length) {
      return 'error'
    }
    return 'ready'
  }

  return { data: parsedData, status: getComponentStatus() }
}
